import { money, rawNumber, type ISODateTime, type Minutes, type Money } from '../domain/branded.js';
import type { Assignment } from '../domain/assignment.js';
import type { Resource } from '../domain/resource.js';
import type { AccrualType, Task } from '../domain/task.js';
import type { ResolvedCalendar } from './calendar-engine.js';
import { workingMinutesBetween } from './calendar-engine.js';
import { resolveResourceRate } from './rate-tables.js';

/** 指派成本的原始分項（尚未套用基準線比較）。 */
export interface AssignmentCostRaw {
  readonly standardCost: Money;
  readonly overtimeCost: Money;
  readonly costPerUseTotal: Money;
  /** = standardCost + overtimeCost + costPerUseTotal。 */
  readonly total: Money;
  readonly actualCost: Money;
  readonly remainingCost: Money;
}
export interface AssignmentLaborCostSegment { readonly start: ISODateTime; readonly finish: ISODateTime; readonly cost: Money; }

/** Rate-aware labour segments for timephased reporting; per-use is excluded. */
export function assignmentLaborCostSegments(
  assignment: Assignment, resource: Resource, includeOvertime: boolean, calendar?: ResolvedCalendar,
): readonly AssignmentLaborCostSegment[] {
  if (resource.type !== 'Work') return [{ start: assignment.start, finish: assignment.finish, cost: resource.type === 'Material'
    ? money(rawNumber(assignment.units) * rawNumber(resolveResourceRate(resource, assignment.rateTable, assignment.start).standardRate)) : assignment.cost }];
  const parts = distributedWork(assignment, resource, calendar);
  const overtimeTotal = includeOvertime ? ((assignment.overtimeWork ?? (0 as Minutes)) as number) : 0;
  const overtimeSlices = (assignment.timephased?.overtimeWork ?? []).flatMap((slice) => splitWork(slice.start, slice.end, slice.valueMinutes as number, assignment, resource, calendar));
  const priced = priceParts(parts, resource, assignment.rateTable, includeOvertime, overtimeSlices, overtimeTotal);
  return parts.map((part, index) => {
    return { start: part.at, finish: part.end, cost: money(priced.partCosts[index] ?? 0) };
  });
}

/**
 * 計算單一指派的成本分項。
 *
 * 型別差異：
 * - Work：(work − overtimeWork) × standardRate + overtimeWork × overtimeRate。
 * - Material：quantity(units) × unitPrice(standardRate 借用為單價)。
 * - Cost：直接採用 `assignment.cost` 作為金額（不隨工期變動）。
 *
 * `costPerUse` 對三種型別都適用，每個指派計一次。
 */
export function computeAssignmentCostRaw(
  assignment: Assignment,
  resource: Resource,
  includeOvertime: boolean,
  task?: Task,
  statusDate?: ISODateTime,
  calendar?: ResolvedCalendar,
): AssignmentCostRaw {
  const initialRate = resolveResourceRate(resource, assignment.rateTable, assignment.start);
  const costPerUseTotal = initialRate.costPerUse;

  let standardCost: Money;
  let overtimeCost: Money;

  if (resource.type === 'Work') {
    const parts = distributedWork(assignment, resource, calendar);
    const overtimeTotal = includeOvertime ? ((assignment.overtimeWork ?? (0 as Minutes)) as number) : 0;
    const overtimeSlices = (assignment.timephased?.overtimeWork ?? []).flatMap((slice) => splitWork(slice.start, slice.end, slice.valueMinutes as number, assignment, resource, calendar));
    const priced = priceParts(parts, resource, assignment.rateTable, includeOvertime, overtimeSlices, overtimeTotal);
    standardCost = money(priced.standard);
    overtimeCost = money(priced.overtime);
  } else if (resource.type === 'Material') {
    const quantity = rawNumber(assignment.units);
    const unitPrice = rawNumber(resolveResourceRate(resource, assignment.rateTable, assignment.start).standardRate);
    standardCost = money(quantity * unitPrice);
    overtimeCost = money(0);
  } else {
    // Cost 型：直接金額，不隨工期／費率變動。
    standardCost = assignment.cost;
    overtimeCost = money(0);
  }

  const total = money((standardCost as number) + (overtimeCost as number) + (costPerUseTotal as number));

  let progress = assignmentProgressRatio(assignment);
  // 狀態日之後的進度尚未「發生」：若任務的實際開始／完成日晚於 statusDate，
  // 視為在狀態日當下尚未開始／完成，即便工時表已回填了更晚的實際數字。
  if (task !== undefined && statusDate !== undefined) {
    if (progress.started && task.actualStart !== null && task.actualStart > statusDate) {
      progress = { ...progress, started: false, ratio: 0 };
    }
    if (progress.finished && task.actualFinish !== null && task.actualFinish > statusDate) {
      progress = { ...progress, finished: false };
    }
  }
  // Imported timephased actual work is authoritative: price those slices at
  // their own effective dates, then apply the normal accrual rule only to the
  // one-per-assignment charge.  Without it, retain the legacy progress split.
  const actualSlices = resource.type === 'Work' ? assignment.timephased?.actualWork ?? [] : [];
  const actualCost = actualSlices.length > 0
    ? money(priceWorkParts(actualSlices.flatMap((slice) => splitWork(slice.start, slice.end, slice.valueMinutes as number, assignment, resource, calendar)), resource, assignment.rateTable, includeOvertime, (assignment.timephased?.overtimeWork ?? []).flatMap((slice) => splitWork(slice.start, slice.end, slice.valueMinutes as number, assignment, resource, calendar))) + (accrueActual(costPerUseTotal, resource.accrueAt, progress) as number))
    : accrueActual(total, resource.accrueAt, progress);
  const remainingCost = money((total as number) - (actualCost as number));

  return { standardCost, overtimeCost, costPerUseTotal, total, actualCost, remainingCost };
}

function priceWorkParts(parts: readonly WorkPart[], resource: Resource, table: Assignment['rateTable'], includeOvertime: boolean, overtimeSlices: readonly { readonly at: ISODateTime; readonly work: number }[]): number {
  const priced = priceParts(parts, resource, table, includeOvertime, overtimeSlices, includeOvertime ? overtimeSlices.reduce((sum, slice) => sum + slice.work, 0) : 0);
  return priced.standard + priced.overtime;
}
function priceParts(parts: readonly WorkPart[], resource: Resource, table: Assignment['rateTable'], includeOvertime: boolean, overtimeSlices: readonly { readonly at: ISODateTime; readonly work: number }[], overtimeTotal: number): { standard: number; overtime: number; partCosts: readonly number[] } {
  let standard = 0;
  let overtime = 0;
  const partCosts = parts.map((part) => {
    const value = part.work * (resolveResourceRate(resource, table, part.at).standardRate as number);
    standard += value;
    return value;
  });
  if (!includeOvertime || overtimeTotal <= 0) return { standard, overtime, partCosts };
  if (overtimeSlices.length > 0) {
    for (const slice of overtimeSlices) {
      const rate = resolveResourceRate(resource, table, slice.at);
      const value = slice.work;
      standard -= value * (rate.standardRate as number);
      overtime += value * (rate.overtimeRate as number);
      const index = parts.findIndex((part) => part.at === slice.at);
      if (index >= 0) partCosts[index] = (partCosts[index] ?? 0) - value * (rate.standardRate as number) + value * (rate.overtimeRate as number);
    }
    return { standard, overtime, partCosts };
  }
  const totalWork = parts.reduce((sum, part) => sum + part.work, 0);
  for (const part of parts) {
    const value = totalWork === 0 ? 0 : overtimeTotal * part.work / totalWork;
    const rate = resolveResourceRate(resource, table, part.at);
    standard -= value * (rate.standardRate as number);
    overtime += value * (rate.overtimeRate as number);
    partCosts[parts.indexOf(part)] = (partCosts[parts.indexOf(part)] ?? 0) - value * (rate.standardRate as number) + value * (rate.overtimeRate as number);
  }
  return { standard, overtime, partCosts };
}
/*
 * Non-timephased work only needs rate boundaries: calendar-engine supplies the
 * exact working-minute weight for each interval, without Date/UTC conversion.
 */
function distributedWork(assignment: Assignment, resource: Resource, calendar?: ResolvedCalendar): readonly WorkPart[] {
  const authoritative = assignment.timephased?.work ?? [];
  if (authoritative.length > 0) return authoritative.flatMap((slice) => splitWork(slice.start, slice.end, slice.valueMinutes as number, assignment, resource, calendar));
  const total = assignment.work as number;
  if (total <= 0) return [];
  return splitWork(assignment.start, assignment.finish, total, assignment, resource, calendar);
}
function splitWork(start: ISODateTime, finish: ISODateTime, total: number, assignment: Assignment, resource: Resource, calendar?: ResolvedCalendar): readonly WorkPart[] {
  const boundaries = [start, finish];
  const selectedRows = resource.rateTables?.[assignment.rateTable ?? 'A'];
  const rows = selectedRows && selectedRows.length > 0 ? selectedRows : resource.rateTables?.A;
  for (const row of rows ?? []) if (row.effectiveFrom > start && row.effectiveFrom < finish) boundaries.push(row.effectiveFrom);
  boundaries.sort();
  const weights = boundaries.slice(0, -1).map((at, i) => calendar ? Math.max(0, workingMinutesBetween(calendar, at, boundaries[i + 1]!) as number) : 1);
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) return boundaries.slice(0, -1).map((at, i) => ({ at, end: boundaries[i + 1]!, work: total / (boundaries.length - 1) }));
  return weights.map((weight, i) => ({ at: boundaries[i]!, end: boundaries[i + 1]!, work: total * weight / sum }));
}
interface WorkPart { readonly at: ISODateTime; readonly end: ISODateTime; readonly work: number; }

/** 指派完成度：0..1。work=0（里程碑型指派）時以 remainingWork 是否已清空判斷。 */
export function assignmentProgressRatio(assignment: Assignment): {
  readonly ratio: number;
  readonly started: boolean;
  readonly finished: boolean;
} {
  const work = assignment.work as number;
  const actualWork = assignment.actualWork as number;
  const remainingWork = assignment.remainingWork as number;

  if (work > 0) {
    const ratio = Math.min(1, Math.max(0, actualWork / work));
    return { ratio, started: actualWork > 0, finished: remainingWork <= 0 };
  }
  // work === 0（例如里程碑指派）：以 remainingWork 是否已清空判斷是否完成。
  const done = remainingWork <= 0;
  return { ratio: done ? 1 : 0, started: done, finished: done };
}

/** 依 accrueAt 與完成度，把 total 拆成「已實際發生」的部分。 */
export function accrueActual(
  total: Money,
  accrueAt: AccrualType,
  progress: { readonly ratio: number; readonly started: boolean; readonly finished: boolean },
): Money {
  const t = total as number;
  if (accrueAt === 'Start') return money(progress.started ? t : 0);
  if (accrueAt === 'End') return money(progress.finished ? t : 0);
  return money(t * progress.ratio);
}

/** Task.fixedCost 依 fixedCostAccrual 與任務完成度計算的「已實際發生」金額。 */
export function accrueFixedCostActual(task: Task, statusDate?: ISODateTime): Money {
  const started = task.actualStart !== null && (statusDate === undefined || task.actualStart <= statusDate);
  const finished = task.actualFinish !== null && (statusDate === undefined || task.actualFinish <= statusDate);
  const ratio = Math.min(1, Math.max(0, (task.percentComplete as number) / 100));
  return accrueActual(task.fixedCost, task.fixedCostAccrual, { ratio, started, finished });
}

/** 兩個 ISODateTime 中較早者。 */
export const earlierOf = (a: ISODateTime, b: ISODateTime): ISODateTime => (a < b ? a : b);
/** 兩個 ISODateTime 中較晚者。 */
export const laterOf = (a: ISODateTime, b: ISODateTime): ISODateTime => (a > b ? a : b);
